import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  NumberFieldControlMessageMeta,
  NumberFieldSingleSelectControlMessageMeta,
  TaskUpdateFieldMeta,
} from 'api/conversations';
import { User, UserType } from 'api/models';
import { TaskType } from 'api/tasks';
import { useUserType } from 'hooks/use-user-type';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { BooleanControlMessage } from './boolean';
import { DateControlMessage } from './date';
import { DateControlMessageDueToDependencies } from './date-change-due-to-dependencies';
import { DateControlMessageDueToNewDependency } from './date-change-due-to-new-dependency';
import { MultiSelectTextControlMessage } from './multi-select-text';
import { NumberControlMessage } from './number';
import { SingleSelectControlMessage } from './text';
import { UserControlMessage } from './user';

export const TaskUpdateFieldRow: FC<{
  messageType: string;
  messageSubType: string;
  meta: TaskUpdateFieldMeta;
  user: User;
}> = ({ messageType, messageSubType, meta, user }) => {
  const userName = fullName(user);
  const keyForString = `${messageType}.${messageSubType}.${meta.fieldDataType}`;
  const currentUserType = useUserType();

  switch (meta.fieldDataType) {
    case 'DATE':
      if (
        (meta.dateShiftAuditMeta?.actualUpdatedTask != null ||
          meta.dateShiftAuditMeta?.actualUpdatedProjectPhase != null) &&
        currentUserType !== UserType.External
      ) {
        return (
          <DateControlMessageDueToDependencies
            rootKey={keyForString}
            meta={meta}
          />
        );
      }
      if (
        ((meta.dateShiftAuditMeta?.sourceDependentTask != null &&
          meta.dateShiftAuditMeta?.targetDependentTask != null) ||
          (meta.dateShiftAuditMeta?.sourceDependentProjectPhase != null &&
            meta.dateShiftAuditMeta?.targetDependentProjectPhase != null)) &&
        currentUserType !== UserType.External
      ) {
        return (
          <DateControlMessageDueToNewDependency
            rootKey={keyForString}
            meta={meta}
          />
        );
      }

      return (
        <DateControlMessage
          rootKey={keyForString}
          userName={userName}
          meta={meta}
        />
      );
    case 'NUMBER':
      switch (meta.fieldType) {
        case 'MULTI_SELECT':
          return (
            <MultiSelectTextControlMessage
              rootKey={keyForString}
              userName={userName}
              meta={meta}
            />
          );
        case 'SINGLE_SELECT':
        default:
          // NOTE: we have a issue in priority field data.
          // In old format value will come as number/string and in new format it will come as object.
          // So we have to support both untill we migrate the old values to new format.

          return typeof meta.newValue === 'number' ||
            typeof meta.newValue === 'string' ? (
            <NumberControlMessage
              rootKey={keyForString}
              userName={userName}
              meta={meta as NumberFieldControlMessageMeta}
            />
          ) : (
            <SingleSelectControlMessage
              rootKey={keyForString}
              userName={userName}
              meta={meta as NumberFieldSingleSelectControlMessageMeta}
            />
          );
      }
    case 'BOOLEAN':
      // NOTE:: This is a work around for fields that don't fall under any conditions above and is only
      // for Milestone and CSAT. Clarify with @ankit/@kanny if required.
      if (meta.fieldName === 'isCsatEnabled') {
        return (
          <FormattedMessage
            id={`${messageType}.${messageSubType}.CSAT.${
              meta.newValue ? 'ADDED' : 'REMOVED'
            }`}
            values={{
              changedByUserName: userName,
            }}
          />
        );
      }

      return (
        <BooleanControlMessage
          rootKey={keyForString}
          userName={userName}
          meta={meta}
        />
      );
    case 'TEXT':
    case 'MULTI_LINE_TEXT':
      // NOTE:: This is a work around for fields that don't fall under any conditions above and is only
      // for Milestone and CSAT. Clarify with @ankit/@kanny if required.
      if (meta.fieldName === 'type') {
        return (
          <FormattedMessage
            id={`${messageType}.${messageSubType}.TASK_TYPE.${
              meta.newValue === TaskType.Milestone ? 'ADDED' : 'REMOVED'
            }`}
            values={{
              changedByUserName: userName,
            }}
          />
        );
      }
      switch (meta.fieldType) {
        case 'MULTI_SELECT':
          return (
            <MultiSelectTextControlMessage
              rootKey={keyForString}
              userName={userName}
              meta={meta}
            />
          );
        case 'SINGLE_SELECT':
        default:
          return (
            <SingleSelectControlMessage
              rootKey={keyForString}
              userName={userName}
              meta={meta}
            />
          );
      }
    case 'USER':
      return (
        <UserControlMessage
          rootKey={keyForString}
          userName={userName}
          meta={meta}
        />
      );

    default:
      return <>{logger.log('TaskUpdateFieldRow', meta)}</>;
  }
};
